import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import { useState } from 'react';
import axios from 'axios';
import { AnimatedSection, AnimatedText, StaggerContainer, StaggerItem } from '../components/AnimatedSection';
import { ArrowRight, CheckCircle, Award, Globe, Users, Heart } from 'lucide-react';

export default function About() {
  const heroRef = useRef(null);
  const { scrollY } = useScroll();
  const heroY = useTransform(scrollY, [0, 500], [0, -80]);

  const values = [
    {
      icon: Award,
      title: 'Estetik Üsteleme',
      desc: 'Her etkinliği, görsel ve duygusal açıdan kusursuz bir sanat eserine dönüştürmek için çalışırız.',
    },
    {
      icon: CheckCircle,
      title: 'Operasyonel Mükemmellik',
      desc: 'Planlama disiplinimiz ve lojistik uzmanlığımız, her etkinliğin sorunsuz ilerlemesini garanti eder.',
    },
    {
      icon: Globe,
      title: 'Ulusal Erişim',
      desc: 'Ankara merkezli olmakla birlikte Türkiye\'nin dört bir yanında aktif olarak hizmet veriyoruz.',
    },
    {
      icon: Heart,
      title: 'Kişisel Dokunuş',
      desc: 'Her proje benzersizdir. Müşterimizin hikayesini, değerlerini ve vizyonunu derinden anlayarak tasarlıyoruz.',
    },
  ];

  const differentiators = [
    'Kurumsal etkinlik ve düğün alanında 8+ yıl deneyim',
    '200\'den fazla başarıyla tamamlanan etkinlik',
    'Türkiye genelinde proje kapasitesi',
    'Tam kapsamlı etkinlik tasarımı ve prodüksiyon',
    'Kurumsal müşteriler için stratejik ortaklık',
    'Etkinlik günü tam saha koordinasyonu',
    'Tedarikçi ve venue ağına özel erişim',
    'Post-etkinlik analiz ve raporlama',
  ];

  return (
    <div style={{ backgroundColor: '#070708' }}>
      {/* ─── HERO ────────────────────────────────────────────────── */}
      <section
        className="relative flex items-end overflow-hidden"
        style={{ minHeight: '60vh', paddingBottom: '5rem', paddingTop: '8rem' }}
        ref={heroRef}
      >
        <motion.div className="absolute inset-0" style={{ y: heroY }}>
          <img
            src="https://images.unsplash.com/photo-1644879131987-e0018885bdb3?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=1800"
            alt="İMER Hakkımızda"
            className="w-full h-full object-cover"
            style={{ transform: 'scale(1.1)' }}
          />
          <div className="absolute inset-0" style={{ background: 'linear-gradient(to right, rgba(7,7,8,0.92) 0%, rgba(7,7,8,0.65) 60%, rgba(7,7,8,0.3) 100%)' }} />
        </motion.div>

        <div className="relative z-10 container-luxury w-full">
          <motion.span
            className="overline mb-5 block"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.7 }}
          >
            Hakkımızda
          </motion.span>
          <motion.h1
            className="display-xl max-w-2xl"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.45, duration: 0.85, ease: [0.22,1,0.36,1] }}
          >
            Zarafetle Tasarlandı.<br />
            <em style={{ color: '#C9A84C', fontStyle: 'italic' }}>Kusursuzlukla Teslim Edildi.</em>
          </motion.h1>
        </div>
      </section>

      {/* ─── BRAND STORY ──────────────────────────────────────────── */}
      <section className="section-py" style={{ backgroundColor: '#0B0B0D' }}>
        <div className="container-luxury">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <AnimatedText>
              <span className="overline mb-5 block">Marka Hikayemiz</span>
              <h2 className="display-lg mb-8">İMER Kimdir?</h2>
            </AnimatedText>

            <AnimatedSection delay={0.2}>
              <p className="body-text mb-6">
                İMER, Ankara'da kurulmuş; Türkiye genelinde kurumsal etkinlikler, düğünler, açılışlar ve özel davetler alanında hizmet veren premium bir etkinlik tasarım ve prodüksiyon markasıdır.
              </p>
              <p className="body-text mb-6">
                Biz, etkinlikleri salt organizasyonun ötesine taşıyan bir marka vizyonuyla çalışıyoruz. Her projeye stratejik bir estetik bakışla yaklaşır; mekân seçiminden sahne tasarımına, ışık atmosferinden misafir deneyimine kadar her detayı titizlikle kurgularız.
              </p>
              <p className="body-text">
                Müşterilerimiz yalnızca sorunsuz bir organizasyon beklemez — aynı zamanda hafizalarda kalan, anlam taşıyan ve markalarını ya da ailelerini gerçek anlamda temsil eden bir deneyim talep eder. İşte tam bu noktada İMER devreye girer.
              </p>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* ─── VALUES ────────────────────────────────────────────────── */}
      <section className="section-py" style={{ backgroundColor: '#070708' }}>
        <div className="container-luxury">
          <AnimatedText className="text-center mb-14">
            <span className="overline mb-4 block">Değerlerimiz</span>
            <h2 className="display-lg">İMER Felsefesi</h2>
          </AnimatedText>

          <StaggerContainer className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value) => (
              <StaggerItem key={value.title}>
                <div
                  className="p-8 h-full flex flex-col"
                  style={{ backgroundColor: '#0E0F12', border: '1px solid rgba(201,168,76,0.08)' }}
                >
                  <value.icon size={22} className="text-[#C9A84C] mb-6 flex-shrink-0" />
                  <h4 className="font-display text-xl font-medium text-[#F5F0E8] mb-3">{value.title}</h4>
                  <p className="font-body text-sm text-[rgba(245,240,232,0.6)] leading-relaxed flex-1">{value.desc}</p>
                </div>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* ─── WHY İMER ───────────────────────────────────────────────── */}
      <section className="section-py" style={{ backgroundColor: '#0B0B0D' }}>
        <div className="container-luxury">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <AnimatedSection>
              <span className="overline mb-5 block">Neden İMER</span>
              <h2 className="display-lg mb-8">İMER'i Tercih Etmek için 8 Neden</h2>
              <ul className="flex flex-col gap-4">
                {differentiators.map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span
                      className="font-body text-xs tracking-[0.1em] text-[#C9A84C] flex-shrink-0 mt-1"
                      style={{ minWidth: '20px' }}
                    >
                      0{i + 1}
                    </span>
                    <span className="font-body text-sm text-[rgba(245,240,232,0.75)] leading-relaxed">{item}</span>
                  </li>
                ))}
              </ul>
            </AnimatedSection>

            <AnimatedSection delay={0.2}>
              <div
                className="relative overflow-hidden"
                style={{ height: '480px' }}
              >
                <img
                  src="https://images.unsplash.com/photo-1651975414388-f33abc8f82a8?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=900"
                  alt="İMER Etkinlik"
                  className="w-full h-full object-cover"
                />
                <div
                  className="absolute inset-0"
                  style={{ background: 'linear-gradient(to bottom, transparent 40%, rgba(11,11,13,0.8) 100%)' }}
                />
                <div
                  className="absolute bottom-8 left-8 right-8 p-6"
                  style={{ background: 'rgba(7,7,8,0.85)', border: '1px solid rgba(201,168,76,0.15)', backdropFilter: 'blur(10px)' }}
                >
                  <p className="font-display text-lg font-light text-[#F5F0E8]" style={{ fontStyle: 'italic' }}>
                    "Ankara'da doğan, Türkiye'de yaşayan bir marka."
                  </p>
                  <span className="font-body text-xs text-[#C9A84C] mt-2 block tracking-[0.12em] uppercase">İMER Events & Weddings</span>
                </div>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* ─── REACH ─────────────────────────────────────────────────── */}
      <section className="section-py" style={{ backgroundColor: '#070708' }}>
        <div className="container-luxury">
          <div
            className="p-10 md:p-16 text-center"
            style={{ border: '1px solid rgba(201,168,76,0.12)', position: 'relative', overflow: 'hidden' }}
          >
            <div
              className="absolute inset-0"
              style={{ background: 'radial-gradient(ellipse 800px 400px at 50% 50%, rgba(201,168,76,0.04), transparent 70%)' }}
            />
            <div className="relative z-10">
              <Globe size={32} className="text-[#C9A84C] mx-auto mb-6" />
              <h2 className="display-lg mb-6">Ankara'dan Türkiye'ye</h2>
              <p className="body-text max-w-xl mx-auto mb-8">
                İMER, Ankara merkezli bir marka olarak Türkiye'nin tüm illerinde etkinlik planlama, tasarım ve prodüksiyon hizmeti sunmaktadır. Başkent'in kalbinden yola çıkarak tüm ülkeye uzanıyoruz.
              </p>
              <div className="flex flex-wrap justify-center gap-8 mb-10">
                {[['Ankara', 'Merkez'], ['İstanbul', 'Aktif Proje'], ['İzmir', 'Aktif Proje'], ['Antalya', 'Aktif Proje'], ['Türkiye', 'Geneli']].map(([city, label]) => (
                  <div key={city} className="text-center">
                    <span className="font-display text-2xl font-medium text-[#C9A84C] block">{city}</span>
                    <span className="font-body text-xs text-[rgba(245,240,232,0.5)] tracking-wide">{label}</span>
                  </div>
                ))}
              </div>
              <Link to="/quote" className="btn-gold text-xs tracking-[0.14em] uppercase inline-block">
                Projenizi Konuşalım
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
